import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { environment } from '../../../../environments/environment';
import { alphabeticValidator } from '../../../motos/utils/remaining-data-form.utils';

/**
 * Validadores personalizados para el componente Cotizador
 * Reutiliza validadores existentes y agrega validaciones específicas
 */
export class CotizadorValidators {
  /**
   * Valida el nombre del cliente
   * - Debe tener al menos 4 caracteres
   * - Solo puede contener letras, espacios y puntos
   * - Reutiliza alphabeticValidator() de remaining-data-form.utils.ts
   */
  static nombre(): ValidatorFn[] {
    return [
      alphabeticValidator(),
      (control: AbstractControl): ValidationErrors | null => {
        const value = control.value?.trim();
        if (!value) {
          return { required: 'El campo nombre es obligatorio' };
        }

        if (value.length < 4) {
          return { minLength: 'Asegúrate de ingresar un nombre válido (mínimo 4 caracteres)' };
        }

        return null;
      }
    ];
  }

  /**
   * Valida la placa del vehículo
   * - Debe cumplir con el patrón: 3 letras + 2 números + 1 letra (ej: ABC12D)
   * - Usa el patrón definido en environment.PATTERN_PLACA
   */
  static placa(): ValidatorFn {
    const pattern = environment.PATTERN_PLACA;

    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value?.trim();

      if (!value) {
        return { required: 'La placa es obligatoria' };
      }

      if (!pattern.test(value.toUpperCase())) {
        return { pattern: 'Ingresa una placa válida (Ej: ABC12D)' };
      }

      return null;
    };
  }

  /**
   * Valida que la confirmación de placa coincida con la placa original
   * @param placaControlName Nombre del control que contiene la placa original
   */
  static placaConfirmation(placaControlName: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return { required: 'Confirma tu placa' };
      }

      const placa = control.parent?.get(placaControlName)?.value;
      const confirPlaca = control.value;

      if (!placa) {
        return null; // Si no hay placa original, no validamos aún
      }

      if (confirPlaca.toUpperCase() !== placa.toUpperCase()) {
        return { mismatch: 'Las placas no coinciden' };
      }

      return null;
    };
  }

  /**
   * Valida el número de celular
   * - Debe tener exactamente 10 dígitos
   * - Solo debe contener números
   * - Debe comenzar con indicativo válido (300-350)
   */
  static celular(): ValidatorFn[] {
    return [
      (control: AbstractControl): ValidationErrors | null => {
        const value = control.value?.toString().trim();

        // 1. Validar campo vacío
        if (!value) {
          return { required: 'El celular es obligatorio' };
        }

        // 2. Validar que solo contenga números
        if (!/^\d+$/.test(value)) {
          return { numeric: 'Este campo solo debe contener dígitos numéricos.' };
        }

        // 3. Validar longitud exacta de 10 dígitos
        if (value.length !== 10) {
          return { exactLength: 'El número debe tener exactamente 10 dígitos.' };
        }

        // 4. Validar indicativo (300-350)
        const prefix = parseInt(value.substring(0, 3), 10);
        if (prefix < 300 || prefix > 350) {
          return { startsWith: 'El número debe comenzar con un indicativo entre 300 y 350.' };
        }

        return null;
      }
    ];
  }

  /**
   * Valida que la ciudad esté en la lista de ciudades disponibles
   * @param ciudades Array de nombres de ciudades válidas
   */
  static ciudad(ciudades: string[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value?.trim();

      if (!value) {
        return { required: 'La ciudad es obligatoria' };
      }

      if (value.length < 3) {
        return { minLength: 'Ingresa al menos 3 caracteres' };
      }

      const exists = ciudades.some(ciudad =>
        ciudad.toLowerCase() === value.toLowerCase()
      );

      if (!exists) {
        return { notFound: 'Selecciona una ciudad de la lista' };
      }

      return null;
    };
  }

  /**
   * Obtiene el mensaje de error para un control específico
   * @param control Control del formulario
   * @returns Mensaje de error o cadena vacía
   */
  static getErrorMessage(control: AbstractControl | null): string {
    if (!control || !control.errors || !control.touched) {
      return '';
    }

    const errors = control.errors;

    // IMPORTANTE: El orden de verificación determina la prioridad de los mensajes

    // 1. REQUIRED - Siempre primero (campo vacío)
    if (errors['required']) return errors['required'];

    // 2. NOMBRE - Validaciones específicas
    if (errors['invalidAlphabetic']) return errors['invalidAlphabetic'];
    if (errors['minLength']) return errors['minLength'];

    // 3. PLACA - Validaciones específicas
    if (errors['pattern']) return errors['pattern'];
    if (errors['mismatch']) return errors['mismatch'];

    // 4. CELULAR - Validaciones específicas
    if (errors['numeric']) return errors['numeric'];
    if (errors['exactLength']) return errors['exactLength'];
    if (errors['startsWith']) return errors['startsWith'];

    // 5. CIUDAD - Validaciones específicas
    if (errors['notFound']) return errors['notFound'];

    // 6. Fallback genérico
    return 'Campo inválido';
  }
}
